import { DestroyRef, inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, filter, fromEvent, Observable, Subject } from 'rxjs';
import { CrossTabMessage, isCrossTabMessage } from './cross-tab-message';

/**
 * Generic cross-tab broadcast service.
 *
 * @remarks
 * Provides type-safe, domain-agnostic cross-tab communication using:
 * - BroadcastChannel API for real-time cross-tab messaging
 * - visibilitychange event for tab focus detection
 *
 * Usage pattern:
 * - Domain services subscribe to `messages$` and filter for their message types
 * - Domain services call `broadcast()` to send messages to other tabs
 * - Domain services subscribe to `onVisibilityChange$` for tab focus events
 *
 * This service contains no domain knowledge - it's a pure infrastructure service
 * that can be reused across any application.
 *
 * @example
 * ```typescript
 * // In a domain service
 * constructor() {
 *   this.#crossTab.messages$
 *     .pipe(
 *       filter(isLoginMessage),
 *       takeUntilDestroyed()
 *     )
 *     .subscribe(msg => this.handleLogin(msg));
 * }
 *
 * login() {
 *   // ... login logic
 *   this.#crossTab.broadcast({ type: 'auth:login', timestamp: Date.now() });
 * }
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class CrossTabBroadcastService {

  /** Channel name for cross-tab broadcasts. */
  static readonly CHANNEL_NAME = 'shopware-auth-state';

  readonly #document = inject(DOCUMENT);
  readonly #destroyRef = inject(DestroyRef);

  #channel: BroadcastChannel | null = null;

  /** Subject for incoming messages from other tabs */
  readonly #messagesSubject = new Subject<CrossTabMessage>();

  /** Subject for visibility change events */
  readonly #visibilityChangeSubject = new Subject<void>();

  /**
   * Observable stream of messages from other tabs.
   *
   * @remarks
   * Emits validated `CrossTabMessage` objects. Domain services should:
   * 1. Subscribe to this observable
   * 2. Filter for their specific message types using type guards
   * 3. Handle messages appropriately
   *
   * Messages that fail validation are silently dropped.
   */
  readonly messages$: Observable<CrossTabMessage> = this.#messagesSubject.asObservable();

  /**
   * Observable that emits when the tab becomes visible.
   *
   * @remarks
   * Debounced by 100ms to prevent rapid-fire events during quick tab switches.
   * Domain services can subscribe to refresh state when tab regains focus.
   */
  readonly onVisibilityChange$: Observable<void> = this.#visibilityChangeSubject.asObservable();

  constructor() {
    this.#initBroadcastChannel();
    this.#initVisibilityChangeHandler();
  }

  /**
   * Broadcasts a message to all other tabs.
   *
   * @remarks
   * Type-safe broadcast - accepts any `CrossTabMessage` subtype.
   * Silently fails if BroadcastChannel is unavailable or message send fails.
   *
   * @param message - The message to broadcast (must include type and timestamp)
   *
   * @example
   * ```typescript
   * // Broadcast login event
   * this.broadcast({ type: 'auth:login', timestamp: Date.now() });
   *
   * // Broadcast with payload
   * this.broadcast({
   *   type: 'i18n:language_changed',
   *   timestamp: Date.now(),
   *   payload: { language: 'de' }
   * });
   * ```
   */
  broadcast<T extends CrossTabMessage>(message: T): void {
    if (!this.#channel) {
      return;
    }

    try {
      this.#channel.postMessage(message);
    } catch {
      // Message send failed (e.g., channel closed)
    }
  }

  /**
   * Initializes the BroadcastChannel for cross-tab communication.
   *
   * @remarks
   * Gracefully handles environments where BroadcastChannel is unavailable.
   */
  #initBroadcastChannel(): void {
    // Check for BroadcastChannel availability (not available in SSR or very old browsers)
    if (typeof BroadcastChannel === 'undefined') {
      return;
    }

    try {
      const channel = new BroadcastChannel(CrossTabBroadcastService.CHANNEL_NAME);
      this.#channel = channel;

      channel.onmessage = (event: MessageEvent<unknown>) => {
        // Validate message structure before emitting
        if (isCrossTabMessage(event.data)) {
          this.#messagesSubject.next(event.data);
        }
      };

      // Cleanup on service destruction
      this.#destroyRef.onDestroy(() => {
        this.#channel?.close();
        this.#channel = null;
        this.#messagesSubject.complete();
        this.#visibilityChangeSubject.complete();
      });
    } catch {
      // BroadcastChannel creation failed (e.g., security restrictions)
      this.#channel = null;
    }
  }

  /**
   * Initializes the visibility change handler.
   *
   * @remarks
   * Emits on `onVisibilityChange$` when tab becomes visible.
   * Debounced to prevent rapid-fire events.
   */
  #initVisibilityChangeHandler(): void {
    fromEvent(this.#document, 'visibilitychange')
      .pipe(
        filter(() => this.#document.visibilityState === 'visible'),
        debounceTime(100), // Prevent rapid-fire on quick tab switches
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(() => {
        this.#visibilityChangeSubject.next();
      });
  }
}
